import { useQuery } from "@apollo/client";
import {
  allTranslations,
  type Language,
  traversePath,
} from "@aurem/translations";
import DOMPurify from "dompurify";
import { useCurrentApp } from "hooks/useCurrentApp";
import { useAuth } from "providers/AuthProvider";
import type { JSX } from "react";
import { useCallback, useMemo } from "react";
import { gql, SupportedLanguages, WebApps } from "types";
import type { TranslationKey, TranslationParams } from "./types";

const TRANSLATIONS_QUERY = gql(/* GraphQL */ `
  query TranslationsQuery(
    $language: SupportedLanguages!
    $application: WebApps!
  ) {
    translations(language: $language, application: $application)
  }
`);

const LINK_PATTERN = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
const DOMPURIFY_CONFIG = {
  ALLOWED_TAGS: ["b", "strong", "i", "em", "a", "br"],
  ALLOWED_ATTR: ["href", "target", "rel", "class"],
};

type UseTranslationsReturn = {
  t: (key: TranslationKey, params?: TranslationParams) => string;
  tRich: (key: TranslationKey, params?: TranslationParams) => JSX.Element;
};

export function useTranslations(language: Language): UseTranslationsReturn {
  const { authState } = useAuth();
  const currentApp = useCurrentApp();
  const application: WebApps =
    currentApp === "employee" ? WebApps.Employee : WebApps.Admin;

  const { data } = useQuery(TRANSLATIONS_QUERY, {
    variables: { language: language as SupportedLanguages, application },
    fetchPolicy: "cache-and-network",
    skip: !currentApp,
  });

  const dynamicTranslations = useMemo((): Record<string, string> => {
    if (!data?.translations) return {};
    try {
      return JSON.parse(data.translations) as Record<string, string>;
    } catch {
      return {};
    }
  }, [data?.translations]);

  const resolveTranslation = useCallback(
    (key: TranslationKey, params: TranslationParams): string => {
      const value = traversePath(allTranslations[language], key);
      if (typeof value === "function") {
        const result =
          params !== undefined && params !== null
            ? (value as (p: TranslationParams) => unknown)(params)
            : (value as () => unknown)();
        return typeof result === "string" ? result : String(result ?? key);
      }
      if (typeof value === "string") return value;
      if (typeof value === "number") return value.toString();
      return key;
    },
    [language],
  );

  const t = useCallback(
    (key: TranslationKey, params?: TranslationParams): string => {
      if (dynamicTranslations[key]) return dynamicTranslations[key];
      return resolveTranslation(key, params);
    },
    [dynamicTranslations, resolveTranslation],
  );

  const tRich = useCallback(
    (key: TranslationKey, params?: TranslationParams): JSX.Element => {
      const raw = dynamicTranslations[key] ?? resolveTranslation(key, params);
      const htmlWithLinks = raw.replace(LINK_PATTERN, (url) => {
        const href = url.startsWith("http") ? url : `https://${url}`;
        return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="hover:underline">${url}</a>`;
      });
      return (
        <span
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(htmlWithLinks, DOMPURIFY_CONFIG),
          }}
        />
      );
    },
    [dynamicTranslations, resolveTranslation],
  );

  return { t, tRich };
}
